import React, { useState } from 'react';
import Modal, { buttonClass, primaryButtonClass } from 'features/dashboards/components/Modal';
import ChartTypePicker from 'features/dashboards/components/ChartTypePicker';
import { RichTextEditor } from 'features/dashboards/components/RichText';
import { errorText } from 'features/dashboards/format';

function useSave(onSave, onClose) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async (patch) => {
    setBusy(true); setError(null);
    try { await onSave(patch); onClose(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  return { busy, error, save };
}

const ErrorLine = ({ error }) => (error
  ? <div className="mx-4 mt-3 rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12px] text-[color:var(--feedback-error)]">{error}</div>
  : null);

/** Draw a card as another chart type. */
export function ChartTypeDialog({ card, onSave, onClose }) {
  const [chart, setChart] = useState(card.viz?.chart || '');
  const { busy, error, save } = useSave(onSave, onClose);
  const { chart: _old, ...rest } = card.viz || {};
  return (
    <Modal title="Chart type" onClose={onClose} width="max-w-lg"
      footer={(
        <>
          <button type="button" className={buttonClass} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => save({ viz: { ...rest, ...(chart ? { chart } : {}) } })}>Save</button>
        </>
      )}>
      <ErrorLine error={error} />
      <div className="px-4 py-3"><ChartTypePicker value={chart} onChange={setChart} /></div>
    </Modal>
  );
}

/** Edit a text card: its words, their styling, and whether it sits in a frame. */
export function TextCardDialog({ card, onSave, onClose }) {
  const [doc, setDoc] = useState(card.spec?.doc || null);
  const [framed, setFramed] = useState(card.viz?.frame !== 'plain');
  const { busy, error, save } = useSave(onSave, onClose);
  const { frame: _f, ...rest } = card.viz || {};
  return (
    <Modal title="Edit text" onClose={onClose} width="max-w-4xl"
      footer={(
        <>
          <label className="mr-auto flex items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
            <input type="checkbox" className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]" checked={framed} onChange={(e) => setFramed(e.target.checked)} />
            Show as a card
          </label>
          <button type="button" className={buttonClass} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={busy || !doc}
            onClick={() => save({ kind: 'text', spec: { doc }, viz: { ...rest, ...(framed ? {} : { frame: 'plain' }) } })}>Save</button>
        </>
      )}>
      <ErrorLine error={error} />
      <div className="px-4 py-3">
        <RichTextEditor initial={card.spec?.doc || card.spec?.markdown || ''} onChange={setDoc} />
      </div>
    </Modal>
  );
}
